import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import fetchTaskListAllPages from '../api/fetchTaskListAllPages';
import MarkdownText from '../components/MarkdownText';
import StatementAttachmentLinks from '../components/StatementAttachmentLinks';
import { useAuth } from '../context/AuthContext';

const EGE_FAMILY_TYPES = buildEgeFamilyTypes();

function buildEgeFamilyTypes() {
  const types = [];
  for (let n = 1; n <= 27; n += 1) {
    if (n === 19) {
      types.push({
        key: '19-21',
        label: '19–21',
        sourceName: 'ЕГЭ 19–21',
        examParam: '19-21',
      });
      continue;
    }
    if (n === 20 || n === 21) continue;
    types.push({
      key: String(n),
      label: String(n),
      sourceName: `ЕГЭ-${n}`,
      examParam: String(n),
    });
    if (n === 7) {
      types.push({
        key: '7-2',
        label: '7-2',
        sourceName: 'ЕГЭ-7-2',
        examParam: '7-2',
      });
    }
  }
  return types;
}

function typeByKey(key) {
  return EGE_FAMILY_TYPES.find((t) => t.key === key) || null;
}

function taskSearchHaystack(task) {
  return [
    task.statement,
    task.title,
    task.source_number,
    task.source_name,
    task.source_author,
    task.ege_family,
  ]
    .map((v) => String(v || '').toLowerCase())
    .join('\n');
}

function polyakovLabel(task) {
  const num = task.source_number != null && String(task.source_number).trim() !== ''
    ? String(task.source_number).trim()
    : '—';
  const src = (task.source_name || '').trim();
  return src ? `${src} №${num}` : `№${num}`;
}

function formatMarkedNumbers(tasks) {
  const nums = tasks.map((t) => {
    const n = String(t.source_number || '').trim();
    return n || String(t.id);
  });
  const shown = nums.slice(0, 24);
  const extra = nums.length > shown.length ? '…' : '';
  return `${shown.join(', ')}${extra}`;
}

function ruPlural(n, one, few, many) {
  const n10 = n % 10;
  const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return one;
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return few;
  return many;
}

/**
 * Раскладка задач банка по семьям внутри типа КИМ.
 */
export default function TaskBankFamiliesPage() {
  const { user } = useAuth();
  const isSiteAdmin = Boolean(user?.is_staff || user?.is_admin);
  const [searchParams, setSearchParams] = useSearchParams();
  const typeKey = (searchParams.get('type') || '').trim();
  const selectedType = typeByKey(typeKey);

  const [tasks, setTasks] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [familiesLoading, setFamiliesLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [markedIds, setMarkedIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [statusMessage, setStatusMessage] = useState('');
  const [newFamilyName, setNewFamilyName] = useState('');
  const [addingFamily, setAddingFamily] = useState(false);
  const [familiesReloadKey, setFamiliesReloadKey] = useState(0);
  const [familyBusyId, setFamilyBusyId] = useState('');
  const [editingFamilyId, setEditingFamilyId] = useState('');
  const [editingFamilyName, setEditingFamilyName] = useState('');
  const [onlyUnassigned, setOnlyUnassigned] = useState(false);

  const markedCount = markedIds.size;
  const searchNorm = search.trim().toLowerCase();

  const unassignedCount = useMemo(
    () => tasks.filter((t) => !(t.ege_family || '').trim()).length,
    [tasks],
  );

  const visibleTasks = useMemo(() => {
    let list = tasks;
    if (onlyUnassigned) {
      list = list.filter((task) => !(task.ege_family || '').trim());
    }
    if (searchNorm) {
      list = list.filter((task) => taskSearchHaystack(task).includes(searchNorm));
    }
    return list;
  }, [tasks, searchNorm, onlyUnassigned]);

  const issueNumbers = useMemo(() => {
    return tasks
      .filter((t) => t.ege_statement_issue)
      .map((t) => {
        const n = String(t.source_number || '').trim();
        return n || `#${t.id}`;
      });
  }, [tasks]);

  const familyLabelById = useMemo(() => {
    const map = new Map();
    families.forEach((f) => map.set(f.id, f.label || f.name || f.id));
    return map;
  }, [families]);

  useEffect(() => {
    setMarkedIds(new Set());
    setSearch('');
    setStatusMessage('');
    setNewFamilyName('');
    setEditingFamilyId('');
    setEditingFamilyName('');
    setFamilyBusyId('');
    setOnlyUnassigned(false);
  }, [typeKey]);

  useEffect(() => {
    if (!selectedType) {
      setFamilies([]);
      setFamiliesLoading(false);
      return undefined;
    }

    let cancelled = false;
    setFamiliesLoading(true);

    api
      .get('/teacher/ege-bank/families/', { params: { exam: selectedType.examParam } })
      .then((res) => {
        if (cancelled) return;
        setFamilies(Array.isArray(res.data?.families) ? res.data.families : []);
      })
      .catch(() => {
        if (cancelled) return;
        setFamilies([]);
        setError((prev) => prev || 'Не удалось загрузить список семей типа.');
      })
      .finally(() => {
        if (!cancelled) setFamiliesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedType, familiesReloadKey]);

  useEffect(() => {
    if (!selectedType) {
      setTasks([]);
      setLoading(false);
      setError('');
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setStatusMessage('');

    const qs = new URLSearchParams();
    qs.set('source_name', selectedType.sourceName);
    qs.append('tag', 'ege');
    qs.set('catalog_sort', 'source');
    qs.set('catalog_order', 'asc');
    qs.set('include_statement', '1');
    qs.set('ege_on_map', '1');
    qs.set('page_size', '200');

    (async () => {
      try {
        // Старая карта (assignments.json) → Task.ege_family, иначе кнопки не горят
        const syncRes = await api.post('/teacher/ege-bank/sync-families/', {
          source_name: selectedType.sourceName,
        });
        const synced = Number(syncRes.data?.updated) || 0;
        if (cancelled) return;
        const list = await fetchTaskListAllPages(`/tasks/?${qs.toString()}`);
        if (cancelled) return;
        setTasks(Array.isArray(list) ? list : []);
        if (synced > 0) {
          setStatusMessage(
            `Подтянуто с журнальной карты: ${synced} ${ruPlural(synced, 'задача', 'задачи', 'задач')}.`,
          );
        }
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Не удалось загрузить задачи этого типа.');
        setTasks([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedType]);

  const setTypeInUrl = useCallback(
    (key) => {
      const next = new URLSearchParams();
      if (key) next.set('type', key);
      setSearchParams(next, { replace: true });
    },
    [setSearchParams],
  );

  function selectType(nextType) {
    if (!nextType) return;
    if (selectedType?.key === nextType.key) return;
    if (markedCount > 0) {
      const ok = window.confirm(
        `Сбросить ${markedCount} ${ruPlural(markedCount, 'пометку', 'пометки', 'пометок')} и перейти к типу ${nextType.label}?`,
      );
      if (!ok) return;
    }
    setTypeInUrl(nextType.key);
  }

  function toggleMarked(taskId) {
    setMarkedIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }

  function setTaskBusy(taskId, busy) {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(taskId);
      else next.delete(taskId);
      return next;
    });
  }

  function patchTaskLocal(taskId, patch) {
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...patch } : t)));
  }

  async function patchTask(taskId, body) {
    setTaskBusy(taskId, true);
    setError('');
    try {
      const res = await api.patch(`/teacher/ege-bank/tasks/${taskId}/`, body);
      return res.data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Не удалось сохранить изменение.');
      throw err;
    } finally {
      setTaskBusy(taskId, false);
    }
  }

  async function setFamily(task, familyId) {
    if (busyIds.has(task.id)) return;
    const current = (task.ege_family || '').trim();
    const nextFamily = current === familyId ? '' : familyId;
    const prev = task.ege_family;
    patchTaskLocal(task.id, { ege_family: nextFamily });
    try {
      const data = await patchTask(task.id, { ege_family: nextFamily });
      patchTaskLocal(task.id, { ege_family: data.ege_family ?? nextFamily });
      const label = nextFamily
        ? (familyLabelById.get(nextFamily) || nextFamily)
        : null;
      setStatusMessage(
        label
          ? `${polyakovLabel(task)} → ${label}`
          : `Семья снята: ${polyakovLabel(task)}`,
      );
    } catch {
      patchTaskLocal(task.id, { ege_family: prev });
    }
  }

  function removeTaskFromList(taskId) {
    setTasks((list) => list.filter((t) => t.id !== taskId));
    setMarkedIds((prev) => {
      const next = new Set(prev);
      next.delete(taskId);
      return next;
    });
  }

  async function excludeFromMap(task) {
    if (busyIds.has(task.id)) return;
    const prevTasks = tasks;
    removeTaskFromList(task.id);
    try {
      await patchTask(task.id, { ege_map_excluded: true });
      setStatusMessage(`Снято с карты: ${polyakovLabel(task)}`);
    } catch {
      setTasks(prevTasks);
    }
  }

  async function markNotEge(task) {
    if (busyIds.has(task.id)) return;
    const prevTasks = tasks;
    removeTaskFromList(task.id);
    try {
      await patchTask(task.id, { ege_not_ege: true });
      setStatusMessage(`Не ЕГЭ (остаётся в банке): ${polyakovLabel(task)}`);
    } catch {
      setTasks(prevTasks);
    }
  }

  async function toggleStatementIssue(task) {
    if (busyIds.has(task.id)) return;
    const next = !task.ege_statement_issue;
    patchTaskLocal(task.id, { ege_statement_issue: next });
    try {
      const data = await patchTask(task.id, { ege_statement_issue: next });
      patchTaskLocal(task.id, {
        ege_statement_issue: Boolean(data.ege_statement_issue),
      });
      setStatusMessage(
        next
          ? `Проблемы с условием: ${polyakovLabel(task)}`
          : `Снята пометка условия: ${polyakovLabel(task)}`,
      );
    } catch {
      patchTaskLocal(task.id, { ege_statement_issue: task.ege_statement_issue });
    }
  }

  async function addFamily(e) {
    e?.preventDefault?.();
    if (!selectedType || addingFamily) return;
    const name = newFamilyName.trim();
    if (!name) {
      setStatusMessage('Введите название новой семьи.');
      return;
    }
    setAddingFamily(true);
    setError('');
    try {
      const res = await api.post('/teacher/ege-bank/families/', {
        exam: selectedType.examParam,
        name,
      });
      const fam = res.data?.family;
      setNewFamilyName('');
      setFamiliesReloadKey((k) => k + 1);
      setStatusMessage(
        fam
          ? `Добавлена семья «${fam.label || fam.name}».`
          : 'Семья добавлена.',
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Не удалось добавить семью.');
    } finally {
      setAddingFamily(false);
    }
  }

  function startRenameFamily(fam) {
    setEditingFamilyId(fam.id);
    setEditingFamilyName(fam.label || fam.name || '');
  }

  function cancelRenameFamily() {
    setEditingFamilyId('');
    setEditingFamilyName('');
  }

  async function saveRenameFamily(e) {
    e?.preventDefault?.();
    if (!selectedType || !editingFamilyId || familyBusyId) return;
    const name = editingFamilyName.trim();
    if (!name) {
      setStatusMessage('Введите новое название семьи.');
      return;
    }
    setFamilyBusyId(editingFamilyId);
    setError('');
    try {
      const res = await api.patch('/teacher/ege-bank/families/', {
        exam: selectedType.examParam,
        id: editingFamilyId,
        name,
      });
      const fam = res.data?.family;
      setEditingFamilyId('');
      setEditingFamilyName('');
      setFamiliesReloadKey((k) => k + 1);
      setStatusMessage(
        fam
          ? `Семья переименована: «${fam.label || fam.name}».`
          : 'Семья переименована.',
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Не удалось переименовать семью.');
    } finally {
      setFamilyBusyId('');
    }
  }

  async function removeFamily(fam) {
    if (!selectedType || familyBusyId) return;
    const label = fam.label || fam.name || fam.id;
    const assigned = tasks.filter((t) => (t.ege_family || '').trim() === fam.id).length;
    const ok = window.confirm(
      assigned > 0
        ? `Удалить семью «${label}»?\nСейчас на неё назначено ${assigned} ${ruPlural(assigned, 'задача', 'задачи', 'задач')} этого типа — назначение снимется.`
        : `Удалить семью «${label}» с кнопок этого типа?`,
    );
    if (!ok) return;

    setFamilyBusyId(fam.id);
    setError('');
    try {
      const res = await api.delete('/teacher/ege-bank/families/', {
        data: {
          exam: selectedType.examParam,
          id: fam.id,
          source_name: selectedType.sourceName,
        },
      });
      const cleared = Number(res.data?.cleared_tasks) || 0;
      setTasks((prev) =>
        prev.map((t) =>
          (t.ege_family || '').trim() === fam.id ? { ...t, ege_family: '' } : t,
        ),
      );
      if (editingFamilyId === fam.id) cancelRenameFamily();
      setFamiliesReloadKey((k) => k + 1);
      setStatusMessage(
        cleared > 0
          ? `Семья «${label}» удалена, снято назначений: ${cleared}.`
          : `Семья «${label}» удалена.`,
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Не удалось удалить семью.');
    } finally {
      setFamilyBusyId('');
    }
  }

  async function deleteMarked() {
    if (!isSiteAdmin || !selectedType || markedCount === 0 || deleting) return;
    const markedTasks = tasks.filter((t) => markedIds.has(t.id));
    const ids = markedTasks.map((t) => t.id);
    const nums = formatMarkedNumbers(markedTasks);
    const ok = window.confirm(
      `Удалить ${ids.length} ${ruPlural(ids.length, 'задачу', 'задачи', 'задач')} типа ${selectedType.label} навсегда из банка?\n№ ${nums}\nСвязанные попытки и пункты списков тоже удалятся.`,
    );
    if (!ok) return;

    setDeleting(true);
    setError('');
    setStatusMessage('');
    try {
      const res = await api.post('/tasks/editor/bulk-delete/', { task_ids: ids });
      const deleted = res.data?.deleted ?? ids.length;
      const gone = new Set(ids);
      setTasks((prev) => prev.filter((t) => !gone.has(t.id)));
      setMarkedIds(new Set());
      setStatusMessage(`Удалено из банка: ${deleted}.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Не удалось удалить помеченные задачи.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="task-bank-cleanup-page task-bank-families-page">
      <nav className="task-cleanup-types" role="tablist" aria-label="Типы КИМ">
        {EGE_FAMILY_TYPES.map((type) => (
          <button
            key={type.key}
            type="button"
            role="tab"
            aria-selected={selectedType?.key === type.key}
            className={`task-cleanup-type-btn${selectedType?.key === type.key ? ' is-active' : ''}`}
            onClick={() => selectType(type)}
          >
            {type.label}
          </button>
        ))}
      </nav>

      <div className="task-cleanup-main">
        <header className="task-cleanup-toolbar">
          <div>
            <h1 className="task-cleanup-title">Раскладка по семьям</h1>
            <p className="task-cleanup-lead text-secondary">
              Выберите тип и назначьте семью. Семьи типа можно добавить, переименовать или удалить блоком ниже.
              {' '}
              <Link to="/teacher/tools">← Инструменты</Link>
            </p>
          </div>
          {isSiteAdmin ? (
            <button
              type="button"
              className="btn task-cleanup-delete-btn"
              disabled={!selectedType || markedCount === 0 || deleting}
              onClick={deleteMarked}
            >
              <i className="fa-solid fa-trash" aria-hidden />
              {deleting
                ? 'Удаление…'
                : markedCount > 0
                  ? `Удалить (${markedCount})`
                  : 'Удалить'}
            </button>
          ) : null}
        </header>

        {error && <div className="alert alert-error">{error}</div>}
        {statusMessage && <div className="alert alert-success">{statusMessage}</div>}

        {selectedType ? (
          <div className="ege-families-manage">
            {families.length > 0 ? (
              <ul className="ege-families-manage-list">
                {families.map((fam) => {
                  const busy = familyBusyId === fam.id;
                  const editing = editingFamilyId === fam.id;
                  return (
                    <li key={fam.id} className="ege-families-manage-item">
                      {editing ? (
                        <form className="ege-families-rename-form" onSubmit={saveRenameFamily}>
                          <input
                            type="text"
                            className="form-input"
                            value={editingFamilyName}
                            onChange={(ev) => setEditingFamilyName(ev.target.value)}
                            disabled={busy}
                            maxLength={120}
                            aria-label="Новое название семьи"
                          />
                          <button
                            type="submit"
                            className="btn btn-outline btn-sm"
                            disabled={busy || !editingFamilyName.trim()}
                          >
                            Сохранить
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            disabled={busy}
                            onClick={cancelRenameFamily}
                          >
                            Отмена
                          </button>
                        </form>
                      ) : (
                        <>
                          <span className="ege-families-manage-name">
                            {fam.label || fam.name || fam.id}
                          </span>
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            disabled={Boolean(familyBusyId)}
                            onClick={() => startRenameFamily(fam)}
                          >
                            Переименовать
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline btn-sm ege-families-delete-btn"
                            disabled={Boolean(familyBusyId)}
                            onClick={() => removeFamily(fam)}
                          >
                            {busy ? '…' : 'Удалить'}
                          </button>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-secondary ege-families-manage-empty">Семей у типа пока нет.</p>
            )}
            <form className="ege-families-add" onSubmit={addFamily}>
              <input
                id="ege-new-family-name"
                type="text"
                className="form-input"
                placeholder="Новая семья"
                value={newFamilyName}
                onChange={(ev) => setNewFamilyName(ev.target.value)}
                disabled={addingFamily}
                maxLength={120}
                aria-label="Название новой семьи"
              />
              <button
                type="submit"
                className="btn btn-outline btn-sm"
                disabled={addingFamily || !newFamilyName.trim()}
              >
                {addingFamily ? '…' : 'Добавить'}
              </button>
            </form>
          </div>
        ) : null}

        {selectedType && issueNumbers.length > 0 ? (
          <div className="ege-families-issues-bar">
            <strong>Проблемы с условием ({issueNumbers.length}):</strong>
            {' '}
            {issueNumbers.join(', ')}
          </div>
        ) : null}

        <div className="task-cleanup-search ege-families-filters">
          <input
            type="search"
            className="form-input"
            placeholder="Поиск по тексту задачи или номеру Полякова"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={!selectedType}
            aria-label="Поиск по тексту задачи"
          />
          <label className={`ege-families-filter-check${!selectedType ? ' is-disabled' : ''}`}>
            <input
              type="checkbox"
              checked={onlyUnassigned}
              onChange={(e) => setOnlyUnassigned(e.target.checked)}
              disabled={!selectedType}
            />
            <span>
              Только задачи не в карте
              {selectedType && !loading
                ? ` (${unassignedCount})`
                : ''}
            </span>
          </label>
        </div>

        {selectedType ? (
          <p className="task-cleanup-meta text-secondary">
            {loading || familiesLoading
              ? `Загрузка типа ${selectedType.label}…`
              : `Тип ${selectedType.label}: показано ${visibleTasks.length} из ${tasks.length} на карте · без семьи ${unassignedCount} · семей ${families.length} · помечено на удаление ${markedCount}`}
          </p>
        ) : (
          <p className="task-cleanup-meta text-secondary">Выберите номер типа, чтобы загрузить задачи.</p>
        )}

        {!selectedType ? null : loading ? (
          <p className="text-secondary">Загрузка задач…</p>
        ) : tasks.length === 0 ? (
          <p className="text-secondary">Нет задач этого типа на карте (или все сняты).</p>
        ) : visibleTasks.length === 0 ? (
          <p className="text-secondary">
            {onlyUnassigned && unassignedCount === 0
              ? 'Все задачи этого типа уже разложены по семьям.'
              : 'Ничего не найдено по текущему фильтру.'}
          </p>
        ) : (
          <ul className="task-cleanup-list">
            {visibleTasks.map((task) => {
              const marked = markedIds.has(task.id);
              const busy = busyIds.has(task.id);
              const currentFamily = (task.ege_family || '').trim();
              const hasFamily = Boolean(currentFamily);
              return (
                <li
                  key={task.id}
                  className={`task-cleanup-card${marked ? ' is-marked' : ''}${task.ege_statement_issue ? ' has-statement-issue' : ''}`}
                >
                  <div className="task-cleanup-card-body">
                    <div className="task-cleanup-card-head">
                      <span className="task-cleanup-polyakov">{polyakovLabel(task)}</span>
                      {hasFamily ? (
                        <span className="ege-families-current text-secondary">
                          {familyLabelById.get(currentFamily) || currentFamily}
                        </span>
                      ) : null}
                    </div>
                    <StatementAttachmentLinks
                      attachments={task.statement_attachments}
                      variant="catalog"
                    />
                    {task.statement ? (
                      <MarkdownText className="statement-text" text={task.statement} />
                    ) : (
                      <p className="text-secondary">Нет текста условия.</p>
                    )}
                  </div>
                  <div className="task-cleanup-card-actions ege-families-card-actions">
                    <div className="ege-families-btns" role="group" aria-label="Семьи">
                      {families.map((fam) => {
                        const isOn = currentFamily === fam.id;
                        const dimmed = hasFamily ? !isOn : true;
                        return (
                          <button
                            key={fam.id}
                            type="button"
                            className={`btn ege-family-btn${isOn ? ' is-on' : ''}${dimmed ? ' is-dim' : ''}`}
                            disabled={busy}
                            onClick={() => setFamily(task, fam.id)}
                            aria-pressed={isOn}
                            title={fam.name || fam.label}
                          >
                            {fam.label || fam.name || fam.id}
                          </button>
                        );
                      })}
                    </div>
                    <div className="ege-families-ops" role="group" aria-label="Действия">
                      <button
                        type="button"
                        className="btn btn-outline ege-exclude-btn"
                        disabled={busy}
                        onClick={() => excludeFromMap(task)}
                      >
                        Снять с карты
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline ege-not-ege-btn"
                        disabled={busy}
                        onClick={() => markNotEge(task)}
                        title="Задача для других наборов, не для ЕГЭ"
                      >
                        не ЕГЭ
                      </button>
                      <button
                        type="button"
                        className={`btn btn-outline ege-issue-btn${task.ege_statement_issue ? ' is-on' : ''}`}
                        disabled={busy}
                        onClick={() => toggleStatementIssue(task)}
                        aria-pressed={Boolean(task.ege_statement_issue)}
                      >
                        {task.ege_statement_issue ? 'Убрать «проблемы»' : 'Проблемы с условием'}
                      </button>
                      {isSiteAdmin ? (
                        <button
                          type="button"
                          className={`btn task-cleanup-mark-btn${marked ? ' is-on' : ''}`}
                          onClick={() => toggleMarked(task.id)}
                          aria-pressed={marked}
                        >
                          <i className="fa-solid fa-trash" aria-hidden />
                          {marked ? 'Снять пометку' : 'Пометить на удаление'}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
